(function () {
  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(window.Shopify && Shopify.locale ? Shopify.locale : "en-US", {
      style: "currency",
      currency: window.Shopify && Shopify.currency && Shopify.currency.active ? Shopify.currency.active : "USD"
    }).format(value);
  };

  const formatShortDate = (date = new Date()) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const getFriendlyPlanName = (planOrName) => {
    const plan = typeof planOrName === "object" && planOrName !== null ? planOrName : null;
    const optionText = plan && Array.isArray(plan.options)
      ? plan.options.map((option) => option && (option.value || option.name || option)).join(" ")
      : "";
    const text = `${optionText} ${String(plan ? plan.name : planOrName || "").trim()}`.trim();
    const lower = text.toLowerCase();
    const daysMatch = lower.match(/(?:every\s+)?(\d+)\s*days?/);
    const weeksMatch = lower.match(/(?:every\s+)?(\d+)\s*weeks?/);
    const monthsMatch = lower.match(/(?:every\s+)?(\d+)\s*months?/);

    if (daysMatch) {
      const days = Number(daysMatch[1]);
      if (days % 30 === 0) {
        const months = days / 30;
        return `Every ${months} ${months === 1 ? "Month" : "Months"}`;
      }
      if (days % 7 === 0) {
        const weeks = days / 7;
        return `Every ${weeks} ${weeks === 1 ? "Week" : "Weeks"}`;
      }
      return `Every ${days} Days`;
    }

    if (weeksMatch) {
      const weeks = Number(weeksMatch[1]);
      return `Every ${weeks} ${weeks === 1 ? "Week" : "Weeks"}`;
    }

    if (monthsMatch) {
      const months = Number(monthsMatch[1]);
      return `Every ${months} ${months === 1 ? "Month" : "Months"}`;
    }

    return text.replace(/^Billing And Delivery\s+/i, "");
  };

  const getPlanIntervalName = (planOrName) => getFriendlyPlanName(planOrName).replace(/^Every\s+/i, "");

  const getVariantQuantity = (variant) => {
    const title = String(variant && variant.title ? variant.title : "");
    const packMatch = title.match(/(\d+)\s*(?:-|\s)?(?:pack|pk|filters?)/i);
    if (packMatch) return Math.max(1, Number(packMatch[1]));

    return Math.max(1, Number(variant && variant.quantity ? variant.quantity : 1));
  };

  const getBasePackTotal = (variants, variant) => {
    const baseVariant = variants.find((item) => getVariantQuantity(item) === 1) || variants.reduce((best, item) => {
      if (!best) return item;
      const bestPerFilter = Number(best.price || 0) / getVariantQuantity(best);
      const itemPerFilter = Number(item.price || 0) / getVariantQuantity(item);
      return itemPerFilter > bestPerFilter ? item : best;
    }, null) || variants[0];
    const baseQuantity = getVariantQuantity(baseVariant);
    const currentQuantity = getVariantQuantity(variant);
    const basePerFilter = Number(baseVariant.price || 0) / baseQuantity;
    return Math.round(basePerFilter * currentQuantity);
  };

  const getPackSavingsPercent = (variants, variant) => {
    const baseTotal = getBasePackTotal(variants, variant);
    const savings = Math.max(0, baseTotal - Number(variant.price || 0));
    return baseTotal > 0 ? Math.round((savings / baseTotal) * 100) : 0;
  };

  const getVariantPlan = (variant, planId) => {
    if (!variant || !Array.isArray(variant.sellingPlans)) return null;
    return variant.sellingPlans.find((plan) => String(plan.id) === String(planId)) || null;
  };

  const getPlanSavingsPercent = (variant, plan) => {
    if (!variant || !plan || !variant.price) return 0;
    return Math.round((Math.max(0, Number(variant.price) - Number(plan.price || 0)) / Number(variant.price)) * 100);
  };

  const getBestPlan = (variant) => {
    if (!variant || !Array.isArray(variant.sellingPlans) || !variant.sellingPlans.length) return null;
    return variant.sellingPlans.reduce((best, plan) => {
      if (!best) return plan;
      return getPlanSavingsPercent(variant, plan) > getPlanSavingsPercent(variant, best) ? plan : best;
    }, null);
  };

  const getSavingsBreakdown = (product, variant, plan, mode) => {
    const quantity = getVariantQuantity(variant);
    const price = mode === "subscription" && plan ? Number(plan.price || 0) : Number(variant.price || 0);
    const basePackTotal = getBasePackTotal(product.variants, variant);
    const compareAtPrice = Math.max(Number(variant.compareAtPrice || 0), Number(variant.price || 0));
    const oneTimeCompareSavings = Math.max(0, compareAtPrice - Number(variant.price || 0));
    const multipackSavings = Math.max(0, basePackTotal - Number(variant.price || 0), oneTimeCompareSavings);
    const subscriberSavings = mode === "subscription" && plan ? Math.max(0, Number(variant.price || 0) - price) : 0;
    const totalSavings = Math.max(0, multipackSavings + subscriberSavings);

    return {
      basePackTotal,
      multipackSavings,
      oneTimeCompareSavings,
      subscriberSavings,
      totalSavings,
      quantity,
      price,
      perFilter: Math.round(price / quantity),
      cashback: Math.round(price * (mode === "subscription" && plan ? 0.05 : 0.03))
    };
  };

  const hideAppPopups = () => {
    const selectors = [
      '[id*="postscript" i]',
      '[class*="postscript" i]',
      '[id*="ps__" i]',
      '[class*="ps__" i]',
      'iframe[src*="postscript" i]',
      '[id*="cookie" i]',
      '[class*="cookie" i]',
      '[id*="consent" i]',
      '[class*="consent" i]',
      '[id*="onetrust" i]',
      '[class*="onetrust" i]',
      '[id*="osano" i]',
      '[class*="osano" i]'
    ];

    document.querySelectorAll(selectors.join(",")).forEach((element) => {
      if (element.closest("[data-sub-app]")) return;
      element.setAttribute("aria-hidden", "true");
      element.style.setProperty("display", "none", "important");
      element.style.setProperty("visibility", "hidden", "important");
      element.style.setProperty("opacity", "0", "important");
      element.style.setProperty("pointer-events", "none", "important");
    });
  };

  const initSubscriptionApps = (scope = document) => {
    scope.querySelectorAll("[data-sub-app]").forEach((root) => {
      if (root.dataset.subAppInitialized === "true") return;
      root.dataset.subAppInitialized = "true";

      const dataScript = root.querySelector("[data-sub-app-products-json]");
      if (!dataScript) return;

      let products = [];
      try {
        products = JSON.parse(dataScript.textContent || "[]");
      } catch (error) {
        products = [];
      }

      if (!products.length) return;

      const state = {
        screen: "intro",
        products,
        productIndex: 0,
        variantIndex: 0,
        mode: "subscription",
        sellingPlanId: null,
        usePurifierImage: false,
        backTarget: null,
        savedItems: []
      };

      const screens = Array.from(root.querySelectorAll("[data-sub-app-screen]"));
      const backButton = root.querySelector("[data-sub-app-back]");
      const productWrap = root.querySelector("[data-sub-app-products]");
      const imageToggle = root.querySelector("[data-sub-app-image-toggle]");
      const packProductEl = root.querySelector("[data-sub-app-pack-product]");
      const variantWrap = root.querySelector("[data-sub-app-variants]");
      const packNextButton = root.querySelector("[data-sub-app-pack-next]");
      const modeButtons = Array.from(root.querySelectorAll("[data-sub-app-mode]"));
      const frequencyWrap = root.querySelector("[data-sub-app-frequencies]");
      const oneTimeCard = root.querySelector("[data-sub-app-onetime-card]");
      const oneTimePrice = root.querySelector("[data-sub-app-onetime-price]");
      const frequencySummary = root.querySelector("[data-sub-app-frequency-summary]");
      const continueButton = root.querySelector("[data-sub-app-continue]");
      const summaryWrap = root.querySelector("[data-sub-app-summary]");
      const addAnotherButton = root.querySelector("[data-sub-app-add-another]");
      const checkoutButton = root.querySelector("[data-sub-app-checkout]");
      const perksWrap = root.querySelector("[data-sub-app-perks]");
      const cancelPerk = root.querySelector("[data-sub-app-cancel-perk]");
      const subscribeSaveButton = root.querySelector("[data-sub-app-subscribe-save]");

      const labels = {
        priceMonth: root.dataset.priceMonthLabel || "Price per month",
        cashback: root.dataset.cashbackLabel || "Cashback earned",
        cashbackInfo: root.dataset.cashbackInfoText || "Get rewarded with 5% back on subscriptions and 3% back on one-time purchases in PuroAir Credits for your next purchase.",
        multipack: root.dataset.multipackDiscountLabel || "Multi-pack discount",
        discount: root.dataset.discountLabel || "Subscriber discount",
        perFilter: root.dataset.perFilterLabel || "Per Filter",
        bestSavings: root.dataset.bestSavingsLabel || "Best Savings",
        replacement: root.dataset.replacementLabel || "Replacement Filter",
        subscription: root.dataset.subscriptionWord || "Subscription",
        firstOrder: root.dataset.firstOrderLabel || "First order will process on",
        delivered: root.dataset.deliveredLabel || "Delivered every",
        recurring: root.dataset.recurringPaymentLabel || "Recurring payment",
        oneTimeSummary: root.dataset.oneTimeSummaryText || "One-time purchase. No recurring deliveries.",
        subscribeSave: root.dataset.subscribeSaveLabel || "Subscribe + Save up to"
      };

      const goTo = (screen) => {
        state.screen = screen;
        render();
        root.scrollIntoView({ block: "start" });
      };

      const getProduct = () => state.products[state.productIndex];
      const getVariant = () => {
        const product = getProduct();
        return product && product.variants[state.variantIndex];
      };
      const getPlan = () => state.mode === "subscription" ? getVariantPlan(getVariant(), state.sellingPlanId) : null;

      const chooseDefaultPlan = () => {
        const variant = getVariant();
        if (!variant || !variant.sellingPlans.length) {
          state.mode = "onetime";
          state.sellingPlanId = null;
          return;
        }
        if (state.mode === "subscription" && !variant.sellingPlans.some((plan) => String(plan.id) === String(state.sellingPlanId))) {
          const bestPlan = getBestPlan(variant);
          state.sellingPlanId = bestPlan ? bestPlan.id : variant.sellingPlans[0].id;
        }
      };

      const getCurrentItem = () => {
        const product = getProduct();
        const variant = getVariant();
        const plan = getPlan();
        if (!product || !variant) return null;
        return {
          key: `${product.id}-${variant.id}-${state.mode}-${plan ? plan.id : "onetime"}-${Date.now()}`,
          product,
          variant,
          mode: state.mode,
          plan,
          savings: getSavingsBreakdown(product, variant, plan, state.mode)
        };
      };

      const getSummaryItems = () => {
        const currentItem = getCurrentItem();
        return currentItem ? state.savedItems.concat(currentItem) : state.savedItems.slice();
      };

      const renderScreens = () => {
        screens.forEach((screen) => {
          screen.classList.toggle("is-active", screen.dataset.subAppScreen === state.screen);
        });
        if (backButton) backButton.hidden = state.screen === "intro";
      };

      const renderProducts = () => {
        productWrap.innerHTML = "";
        state.products.forEach((product, index) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "sub-app-product";
          button.dataset.subAppProductIndex = String(index);
          const image = state.usePurifierImage && product.purifierImage ? product.purifierImage : product.image;
          button.innerHTML = `
            <span class="sub-app-product__media">
              ${image ? `<img src="${image}" alt="${product.title}">` : ""}
            </span>
            <span>${product.title}</span>
          `;
          button.addEventListener("click", () => {
            state.productIndex = index;
            state.variantIndex = 0;
            state.sellingPlanId = null;
            state.mode = "subscription";
            chooseDefaultPlan();
            goTo("pack");
          });
          productWrap.appendChild(button);
        });

        if (imageToggle) {
          imageToggle.textContent = state.usePurifierImage
            ? imageToggle.getAttribute("data-default-label")
            : imageToggle.getAttribute("data-purifier-label");
        }
      };

      const renderVariants = () => {
        const product = getProduct();
        if (packProductEl) packProductEl.textContent = product ? `${product.title} ${labels.replacement}s` : "";
        variantWrap.innerHTML = "";
        if (!product) return;

        product.variants.forEach((variant, index) => {
          const savingsPercent = getPackSavingsPercent(product.variants, variant);
          const button = document.createElement("button");
          button.type = "button";
          button.className = `sub-app-choice${index === state.variantIndex ? " is-active" : ""}`;
          button.setAttribute("aria-pressed", index === state.variantIndex ? "true" : "false");
          button.innerHTML = `
            <span>${variant.title}</span>
            ${savingsPercent > 0 ? `<span class="sub-app-choice__badge">SAVE ${savingsPercent}%</span>` : "<span></span>"}
          `;
          button.addEventListener("click", () => {
            state.variantIndex = index;
            state.sellingPlanId = null;
            chooseDefaultPlan();
            goTo("frequency");
          });
          variantWrap.appendChild(button);
        });
      };

      const renderFrequencies = () => {
        const product = getProduct();
        const variant = getVariant();
        const plan = getPlan();
        const savings = product && variant ? getSavingsBreakdown(product, variant, plan, state.mode) : null;

        modeButtons.forEach((button) => {
          const active = button.dataset.subAppMode === state.mode;
          button.classList.toggle("is-active", active);
          button.setAttribute("aria-pressed", active ? "true" : "false");
        });

        frequencyWrap.innerHTML = "";
        frequencyWrap.hidden = state.mode !== "subscription";
        oneTimeCard.hidden = state.mode !== "onetime";

        if (!variant) return;

        if (state.mode === "subscription") {
          if (!variant.sellingPlans.length) {
            const empty = document.createElement("div");
            empty.className = "sub-app-empty";
            empty.textContent = root.getAttribute("data-no-selling-plans-text") || "No subscription plans are available for this variant.";
            frequencyWrap.appendChild(empty);
          } else {
            const plansWithSavings = variant.sellingPlans.map((item) => ({
              ...item,
              computedSavingsPercent: getPlanSavingsPercent(variant, item)
            }));
            const maxSavingsPercent = Math.max(0, ...plansWithSavings.map((item) => Number(item.computedSavingsPercent || 0)));
            const bestPlan = plansWithSavings.find((item) => Number(item.computedSavingsPercent || 0) === maxSavingsPercent);

            plansWithSavings.forEach((item) => {
              const active = String(item.id) === String(state.sellingPlanId);
              const button = document.createElement("button");
              button.type = "button";
              button.className = `sub-app-frequency${active ? " is-active" : ""}`;
              button.setAttribute("aria-pressed", active ? "true" : "false");
              button.innerHTML = `
                <span>${getFriendlyPlanName(item)}</span>
                ${
                  bestPlan && String(item.id) === String(bestPlan.id) && item.computedSavingsPercent > 0
                    ? `<span class="sub-app-frequency__badge"><span class="sub-app-frequency__badge-label">${labels.bestSavings}</span><span>-${item.computedSavingsPercent}%</span></span>`
                    : "<span></span>"
                }
              `;
              button.addEventListener("click", () => {
                state.sellingPlanId = item.id;
                render();
              });
              frequencyWrap.appendChild(button);
            });
          }
        }

        if (oneTimePrice && savings) oneTimePrice.textContent = formatMoney(savings.price);

        if (frequencySummary && savings) {
          if (state.mode === "subscription" && plan) {
            frequencySummary.innerHTML = `
              ${savings.totalSavings > 0 ? `<span class="sub-app-frequency-summary__save">SAVING ${formatMoney(savings.totalSavings)}</span>` : ""}
              <div class="sub-app-frequency-summary__row"><span>${labels.priceMonth}</span><strong>${formatMoney(savings.perFilter)}</strong></div>
              <div class="sub-app-frequency-summary__row cashback__row"><span>${labels.cashback}</span><strong>${formatMoney(savings.cashback)}</strong></div>
              <div class="sub-app-frequency-summary__price">
                <span>${formatMoney(savings.price)}</span>
                <small>/ ${getPlanIntervalName(plan).toLowerCase()}</small>
              </div>
            `;
          } else {
            frequencySummary.innerHTML = "";
          }
        }

        if (continueButton) {
          continueButton.textContent = state.mode === "onetime"
            ? continueButton.getAttribute("data-onetime-label") || "Add to Cart"
            : continueButton.getAttribute("data-continue-label") || "Continue";
        }
      };

      const renderSummary = () => {
        const items = getSummaryItems();
        summaryWrap.innerHTML = "";
        const hasSubscription = items.some((item) => item.mode === "subscription");
        const oneTimeUpsells = items.reduce((upsells, item, index) => {
          if (item.mode === "subscription" || !item.variant.sellingPlans.length) return upsells;
          const bestPlan = getBestPlan(item.variant);
          if (!bestPlan) return upsells;
          const subscriptionSavings = Math.max(0, Number(item.variant.price || 0) - Number(bestPlan.price || 0));
          if (subscriptionSavings <= 0) return upsells;
          upsells.push({ index, item, bestPlan, subscriptionSavings });
          return upsells;
        }, []);

        const chooseSubscriptionFrequencyForItem = (index, item, bestPlan) => {
          if (index < state.savedItems.length) {
            state.savedItems.splice(index, 1);
          }
          const productIndex = state.products.findIndex((entry) => String(entry.id) === String(item.product.id));
          const variantIndex = item.product.variants.findIndex((entry) => String(entry.id) === String(item.variant.id));
          state.productIndex = productIndex >= 0 ? productIndex : state.productIndex;
          state.variantIndex = variantIndex >= 0 ? variantIndex : state.variantIndex;
          state.mode = "subscription";
          state.sellingPlanId = bestPlan.id;
          state.backTarget = "summary";
          goTo("frequency");
        };

        items.forEach((item, index) => {
          const { product, variant, plan, mode, savings } = item;
          const perText = mode === "subscription" && plan ? `/ ${getPlanIntervalName(plan).toLowerCase()}` : "";
          const itemUpsell = oneTimeUpsells.find((upsell) => upsell.index === index);
          const group = document.createElement("div");
          group.className = "sub-app-summary-group";
          const card = document.createElement("div");
          card.className = "sub-app-summary-card";
          card.innerHTML = `
            <div class="sub-app-summary__price">
              <span>${formatMoney(savings.price)}</span>
              <small>${perText}</small>
            </div>
            ${savings.totalSavings > 0 ? `<span class="sub-app-summary__save">SAVE ${formatMoney(savings.totalSavings)}</span>` : ""}
            <div class="sub-app-summary__row"><span>${product.title} x ${savings.quantity}</span><span>${formatMoney(savings.basePackTotal)}</span></div>
            ${savings.multipackSavings > 0 ? `<div class="sub-app-summary__row sub-app-summary__row--discount"><span>${labels.multipack}</span><span>-${formatMoney(savings.multipackSavings)}</span></div>` : ""}
            ${mode === "subscription" ? `<div class="sub-app-summary__row sub-app-summary__row--discount"><span>${labels.discount}</span><span>${savings.subscriberSavings > 0 ? `-${formatMoney(savings.subscriberSavings)}` : formatMoney(0)}</span></div>` : ""}
            <div class="sub-app-summary__row sub-app-summary__row--per-filter"><span>${labels.perFilter}</span><strong>${formatMoney(savings.perFilter)}</strong></div>
            <div class="sub-app-summary__row sub-app-summary__row--rewards">
              <span class="sub-app-summary__label-with-info">
                ${labels.cashback}
                <button class="sub-app-info-button" type="button" data-sub-app-cashback-info aria-expanded="false" aria-label="${labels.cashback} info">i</button>
              </span>
              <strong>${formatMoney(savings.cashback)}</strong>
            </div>
            <div class="sub-app-cashback-info" data-sub-app-cashback-panel hidden>${labels.cashbackInfo}</div>
          `;
          group.appendChild(card);

          const note = document.createElement("div");
          note.className = "sub-app-summary-item";
          note.innerHTML = `
            <span class="sub-app-summary-item__dot" aria-hidden="true"></span>
            <div>
              <strong class="sub-app-summary-item__title">${product.title} ${labels.replacement}${mode === "subscription" ? ` ${labels.subscription}` : ""}</strong>
              ${
                mode === "subscription" && plan
                  ? `<p class="sub-app-summary-item__line"><span>${labels.firstOrder}</span><strong>${formatShortDate()}</strong></p><p class="sub-app-summary-item__line"><span>${labels.delivered}</span><strong>${getPlanIntervalName(plan).toLowerCase()}</strong></p><p class="sub-app-summary-item__line"><span>${labels.recurring}</span><strong>${formatMoney(savings.price)}</strong></p>`
                  : `<p>${labels.oneTimeSummary}</p>`
              }
            </div>
          `;
          group.appendChild(note);

          if (oneTimeUpsells.length > 1 && itemUpsell) {
            const itemSaveButton = document.createElement("button");
            itemSaveButton.type = "button";
            itemSaveButton.className = "sub-app-item-save-link";
            itemSaveButton.textContent = `${labels.subscribeSave} ${formatMoney(itemUpsell.subscriptionSavings)}`;
            itemSaveButton.addEventListener("click", () => {
              chooseSubscriptionFrequencyForItem(index, item, itemUpsell.bestPlan);
            });
            group.appendChild(itemSaveButton);
          }

          summaryWrap.appendChild(group);
        });

        if (cancelPerk) cancelPerk.hidden = !hasSubscription;
        if (perksWrap) perksWrap.classList.toggle("sub-app-perks--single", !hasSubscription);

        if (subscribeSaveButton) {
          const globalUpsell = oneTimeUpsells.length === 1 ? oneTimeUpsells[0] : null;
          subscribeSaveButton.hidden = !globalUpsell;
          if (globalUpsell) {
            subscribeSaveButton.textContent = `${labels.subscribeSave} ${formatMoney(globalUpsell.subscriptionSavings)}`;
            subscribeSaveButton.onclick = () => {
              chooseSubscriptionFrequencyForItem(globalUpsell.index, globalUpsell.item, globalUpsell.bestPlan);
            };
          } else {
            subscribeSaveButton.onclick = null;
          }
        }
      };

      const render = () => {
        chooseDefaultPlan();
        renderScreens();
        renderProducts();
        renderVariants();
        renderFrequencies();
        if (state.screen === "summary") renderSummary();
      };

      root.querySelectorAll("[data-sub-app-go]").forEach((button) => {
        button.addEventListener("click", () => goTo(button.dataset.subAppGo));
      });

      if (backButton) {
        backButton.addEventListener("click", () => {
          if (state.backTarget && state.screen === "frequency") {
            const target = state.backTarget;
            state.backTarget = null;
            goTo(target);
            return;
          }

          const order = ["intro", "filter", "pack", "frequency", "summary"];
          const currentIndex = order.indexOf(state.screen);
          goTo(order[Math.max(0, currentIndex - 1)] || "intro");
        });
      }

      if (imageToggle) {
        imageToggle.addEventListener("click", () => {
          state.usePurifierImage = !state.usePurifierImage;
          renderProducts();
        });
      }

      modeButtons.forEach((button) => {
        button.addEventListener("click", () => {
          state.mode = button.dataset.subAppMode;
          chooseDefaultPlan();
          render();
        });
      });

      if (continueButton) {
        continueButton.addEventListener("click", () => {
          goTo("summary");
        });
      }

      if (packNextButton) {
        packNextButton.addEventListener("click", () => {
          chooseDefaultPlan();
          goTo("frequency");
        });
      }

      if (summaryWrap) {
        summaryWrap.addEventListener("click", (event) => {
          const infoButton = event.target.closest("[data-sub-app-cashback-info]");
          if (!infoButton) return;

          const panel = infoButton.closest(".sub-app-summary-card").querySelector("[data-sub-app-cashback-panel]");
          if (!panel) return;

          const shouldOpen = panel.hidden;
          panel.hidden = !shouldOpen;
          infoButton.setAttribute("aria-expanded", shouldOpen ? "true" : "false");
        });
      }

      if (addAnotherButton) {
        addAnotherButton.addEventListener("click", () => {
          const item = getCurrentItem();
          if (item) state.savedItems.push(item);
          state.productIndex = 0;
          state.variantIndex = 0;
          state.mode = "subscription";
          state.sellingPlanId = null;
          state.backTarget = null;
          chooseDefaultPlan();
          goTo("filter");
        });
      }

      if (checkoutButton) {
        checkoutButton.addEventListener("click", async () => {
          const items = getSummaryItems().map((item) => {
            const payload = {
              id: item.variant.id,
              quantity: 1
            };
            if (item.mode === "subscription" && item.plan) payload.selling_plan = item.plan.id;
            return payload;
          });

          if (!items.length) return;

          checkoutButton.disabled = true;
          checkoutButton.classList.add("is-loading");
          checkoutButton.querySelector("span").textContent = checkoutButton.getAttribute("data-loading-label") || "Preparing Checkout";

          try {
            const clearResponse = await fetch("/cart/clear.js", {
              method: "POST",
              headers: {
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest"
              }
            });

            if (!clearResponse.ok) throw new Error("Unable to clear cart.");

            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest"
              },
              body: JSON.stringify({ items })
            });

            if (!response.ok) throw new Error("Unable to add subscription items.");
            window.location.href = "/checkout";
          } catch (error) {
            checkoutButton.disabled = false;
            checkoutButton.classList.remove("is-loading");
            checkoutButton.querySelector("span").textContent = checkoutButton.getAttribute("data-error-label") || "Please try again";
            window.setTimeout(() => {
              checkoutButton.querySelector("span").textContent = checkoutButton.getAttribute("data-label") || "Checkout Now";
            }, 2200);
          }
        });
      }

      render();
      hideAppPopups();
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initSubscriptionApps();
      hideAppPopups();
    });
  } else {
    initSubscriptionApps();
    hideAppPopups();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initSubscriptionApps(event.target);
    hideAppPopups();
  });

  const popupObserver = new MutationObserver(() => {
    if (document.querySelector("[data-sub-app]")) hideAppPopups();
  });
  popupObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
